import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDownload, apiFetch } from '../api/client';
import type { ContentItem, ExportPreflight, ExportStandard, VariantList } from '../api/types';
import { useApi, useMutation } from '../lib/use-api';
import { formatBytes, languageName } from '../lib/format';
import { ErrorNotice } from './Feedback';

const STANDARDS: ExportStandard[] = ['scorm12', 'scorm2004', 'xapi', 'cmi5', 'html'];

/**
 * Cấu hình và tải gói học liệu — plan §5.1 ("nút Export, chọn chuẩn + tham số").
 *
 * Gói dựng ở package-builder; delivery-api ép quyền, kiểm tham số và chuyển
 * tiếp. Trang này KHÔNG gọi thẳng package-builder: một origin, một token.
 *
 * Danh sách ngôn ngữ lấy từ server chứ không để người dùng gõ — gói song ngữ
 * gom `content/<lang>/` theo bản dịch có thật (§2.3), khai ngôn ngữ không tồn
 * tại chỉ tạo thư mục rỗng trong gói.
 */
export function ExportDialog({
  content,
  onClose,
}: {
  content: ContentItem;
  onClose: () => void;
}): ReactNode {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = i18n.resolvedLanguage ?? 'vi';
  const dialogRef = useRef<HTMLDialogElement>(null);

  const [standard, setStandard] = useState<ExportStandard>('scorm12');
  const [masteryScore, setMasteryScore] = useState('70');
  const [completionRule, setCompletionRule] = useState<'onFinish' | 'onScore' | 'onView'>(
    'onFinish',
  );
  const [allowResume, setAllowResume] = useState(true);
  const [reportSubQuestions, setReportSubQuestions] = useState(false);
  const [languages, setLanguages] = useState<string[]>([content.language]);
  const [defaultLanguage, setDefaultLanguage] = useState(content.language);
  const [languageSelector, setLanguageSelector] = useState<'none' | 'auto' | 'manual' | 'both'>(
    'auto',
  );

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const variants = useApi<VariantList>(`/api/admin/content/${content.contentId}/variants`);

  // Tiêu đề theo từng ngôn ngữ lấy từ chính bản dịch tương ứng. Dùng tiêu đề
  // của bản gốc cho mọi ngôn ngữ sẽ làm tên bài trên LMS sai ngôn ngữ.
  const titleByLanguage = useMemo(() => {
    const map: Record<string, string> = { [content.language]: content.title };
    for (const variant of variants.data?.items ?? []) map[variant.language] = variant.title;
    return map;
  }, [content.language, content.title, variants.data]);

  const availableLanguages = useMemo(() => {
    const set = new Set<string>([content.language]);
    for (const variant of variants.data?.items ?? []) set.add(variant.language);
    return [...set].sort((a, b) =>
      languageName(a, locale).localeCompare(languageName(b, locale), locale),
    );
  }, [content.language, variants.data, locale]);

  const options = useMemo(
    () => ({
      standard,
      masteryScore: Number(masteryScore),
      completionRule,
      allowResume,
      reportSubQuestions,
      languages,
      defaultLanguage,
      languageSelector,
      title: Object.fromEntries(
        languages.map((lang) => [lang, titleByLanguage[lang] ?? content.title]),
      ),
    }),
    [
      standard,
      masteryScore,
      completionRule,
      allowResume,
      reportSubQuestions,
      languages,
      defaultLanguage,
      languageSelector,
      titleByLanguage,
      content.title,
    ],
  );

  const [checked, setChecked] = useState<ExportPreflight | undefined>(undefined);

  const preflight = useMutation<void, ExportPreflight>(
    useCallback(
      () =>
        apiFetch<ExportPreflight>(`/api/admin/content/${content.contentId}/package/preflight`, {
          method: 'POST',
          body: options,
        }),
      [content.contentId, options],
    ),
  );

  // Kết quả kiểm cũ không còn đúng khi đổi tham số — giữ lại là hiển thị số
  // liệu của một cấu hình khác, mà người dùng sẽ tin là của cấu hình hiện tại.
  useEffect(() => {
    setChecked(undefined);
  }, [options]);

  const runPreflight = async (): Promise<void> => {
    const result = await preflight.run();
    if (result) setChecked(result);
  };

  const download = useMutation<void, void>(
    useCallback(
      () =>
        apiDownload(`/api/admin/content/${content.contentId}/package`, {
          method: 'POST',
          body: options,
          fallbackFileName: `${content.contentId}-${standard}.zip`,
        }),
      [content.contentId, options, standard],
    ),
  );

  const toggleLanguage = (lang: string): void => {
    setLanguages((current) => {
      if (current.includes(lang)) {
        // Không cho bỏ ngôn ngữ cuối cùng: gói không có ngôn ngữ nào là gói rỗng.
        if (current.length === 1) return current;
        const next = current.filter((item) => item !== lang);
        // Ngôn ngữ mặc định phải nằm trong danh sách, nếu không server từ chối.
        if (lang === defaultLanguage) setDefaultLanguage(next[0] ?? '');
        return next;
      }
      return [...current, lang];
    });
  };

  // Chuẩn không có khái niệm điểm đạt thì không hỏi — hiện ô vô nghĩa chỉ làm
  // người dùng tưởng cấu hình đó có tác dụng.
  const supportsMastery = standard !== 'html';
  const supportsResume = standard !== 'html';

  return (
    <dialog ref={dialogRef} className="dialog dialog--wide dialog--export" onClose={onClose}>
      <h2>{t('export.heading', { title: content.title })}</h2>

      <label className="field">
        <span>{t('export.standard')}</span>
        <select
          value={standard}
          onChange={(event) => setStandard(event.target.value as ExportStandard)}
        >
          {STANDARDS.map((value) => (
            <option key={value} value={value}>
              {t(`export.standardName.${value}`)}
            </option>
          ))}
        </select>
        <small className="hint">{t(`export.standardHint.${standard}`)}</small>
      </label>

      {supportsMastery ? (
        <>
          <label className="field">
            <span>{t('export.masteryScore')}</span>
            <input
              type="number"
              min={0}
              max={100}
              value={masteryScore}
              onChange={(event) => setMasteryScore(event.target.value)}
            />
          </label>

          <label className="field">
            <span>{t('export.completionRule')}</span>
            <select
              value={completionRule}
              onChange={(event) =>
                setCompletionRule(event.target.value as 'onFinish' | 'onScore' | 'onView')
              }
            >
              <option value="onFinish">{t('export.completionRuleName.onFinish')}</option>
              <option value="onScore">{t('export.completionRuleName.onScore')}</option>
              <option value="onView">{t('export.completionRuleName.onView')}</option>
            </select>
          </label>
        </>
      ) : null}

      {supportsResume ? (
        <label className="field field--inline">
          <input
            type="checkbox"
            checked={allowResume}
            onChange={(event) => setAllowResume(event.target.checked)}
          />
          <span>{t('export.allowResume')}</span>
        </label>
      ) : null}

      <label className="field field--inline">
        <input
          type="checkbox"
          checked={reportSubQuestions}
          onChange={(event) => setReportSubQuestions(event.target.checked)}
        />
        <span>{t('export.reportSubQuestions')}</span>
      </label>

      <fieldset className="field">
        <legend>{t('export.languages')}</legend>
        {variants.loading ? <p className="hint">{t('common:state.loading')}</p> : null}
        {availableLanguages.map((lang) => (
          <label key={lang} className="field--inline">
            <input
              type="checkbox"
              checked={languages.includes(lang)}
              onChange={() => toggleLanguage(lang)}
            />
            <span>{languageName(lang, locale)}</span>
          </label>
        ))}
        <small className="hint">{t('export.languagesHint')}</small>
      </fieldset>

      <label className="field">
        <span>{t('export.defaultLanguage')}</span>
        <select
          value={defaultLanguage}
          onChange={(event) => setDefaultLanguage(event.target.value)}
        >
          {languages.map((lang) => (
            <option key={lang} value={lang}>
              {languageName(lang, locale)}
            </option>
          ))}
        </select>
      </label>

      {languages.length > 1 ? (
        <label className="field">
          <span>{t('export.languageSelector')}</span>
          <select
            value={languageSelector}
            onChange={(event) =>
              setLanguageSelector(event.target.value as 'none' | 'auto' | 'manual' | 'both')
            }
          >
            <option value="auto">{t('export.languageSelectorName.auto')}</option>
            <option value="manual">{t('export.languageSelectorName.manual')}</option>
            <option value="both">{t('export.languageSelectorName.both')}</option>
            <option value="none">{t('export.languageSelectorName.none')}</option>
          </select>
        </label>
      ) : null}

      {checked ? (
        <div className="notice">
          <p>
            {t('export.preflightResult', {
              filename: checked.filename,
              size: formatBytes(checked.sizeBytes, locale),
            })}
          </p>
          {checked.warnings.length > 0 ? (
            <ul className="warning-list">
              {checked.warnings.map((code) => (
                // Backend trả mã, UI dịch — không nhận chuỗi tiếng Việt cứng (§6).
                <li key={code}>{t(`export.warning.${code}`, { defaultValue: code })}</li>
              ))}
            </ul>
          ) : (
            <p className="hint">{t('export.noWarnings')}</p>
          )}
        </div>
      ) : null}

      {preflight.error ? <ErrorNotice error={preflight.error} /> : null}
      {download.error ? <ErrorNotice error={download.error} /> : null}

      <footer className="dialog__footer">
        <button type="button" onClick={() => dialogRef.current?.close()}>
          {t('common:action.cancel')}
        </button>
        <button
          type="button"
          disabled={preflight.pending || languages.length === 0}
          onClick={() => void runPreflight()}
        >
          {t('export.checkAction')}
        </button>
        <button
          type="button"
          disabled={download.pending || languages.length === 0}
          onClick={() => void download.run()}
        >
          {download.pending ? t('export.building') : t('export.downloadAction')}
        </button>
      </footer>
    </dialog>
  );
}
